import { parseAsString, useQueryState } from 'nuqs';

/**
 * Custom hook to manage clinicalTrialJobId state with URL synchronization using nuqs.
 *
 * This hook handles clinicalTrialJobId from clinical trial job results.
 * The clinicalTrialJobId is passed in the URL and used by the backend to fetch
 * patient IDs from the clinical trial job's execution results.
 *
 * @example
 * ```tsx
 * const { clinicalTrialJobId, hasClinicalTrialJobId, clearClinicalTrialJobId } = useCohortClinicalTrialJobId();
 *
 * // Pass clinicalTrialJobId to patient list API
 * const { data } = useGetPatientListQuery({ clinicalTrialJobId });
 * ```
 */
export function useCohortClinicalTrialJobId() {
    // Use nuqs for URL-synced state management
    // parseAsString returns null for missing values (no default needed)
    const [clinicalTrialJobId, setClinicalTrialJobId] = useQueryState(
        'clinicalTrialJobId',
        parseAsString.withOptions({
            history: 'replace', // Use replace to avoid polluting browser history
            shallow: false, // Ensure router is aware of changes
        })
    );

    return {
        /**
         * Current clinicalTrialJobId value (null if not set)
         */
        clinicalTrialJobId,
        /**
         * Set the clinicalTrialJobId (updates URL automatically)
         */
        setClinicalTrialJobId,
        /**
         * Convenience boolean to check if clinicalTrialJobId exists
         */
        hasClinicalTrialJobId: !!clinicalTrialJobId,
        /**
         * Clear the clinicalTrialJobId from URL
         */
        clearClinicalTrialJobId: () => setClinicalTrialJobId(null),
    };
}
